"use client";

import { ChangePasswordForm, PasswordDots } from "@repo/components";
import { PROFILE_FORM_FIELDS } from "@repo/constants/account";
import { useFormValidation, useSnackbarHandler } from "@repo/lib/hooks/common";
import { useTrackProfileUpdate, useUserAttributes } from "@repo/lib/hooks/webengage";
import { validation } from "@repo/lib/utils";
import { updateCustomerProfile } from "@repo/server-actions/account";
import { Button, ErrorMessage, Input, LoadingDots, OffCanvasDrawer, Select } from "@repo/ui";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useCallback, useState, useTransition } from "react";
const UpdateProfileForm = ({
  formFields,
  session
}) => {
  const {
    update
  } = useSession();
  const router = useRouter();
  const t = useTranslations("Common");
  const [openChangePassword, setChangePassword] = useState(false);
  const [isPending, startTransition] = useTransition();
  const {
    formValues,
    formErrors,
    setFormErrors,
    handleValidation
  } = useFormValidation(formFields);
  const {
    showSnackbar
  } = useSnackbarHandler();
  const {
    trackProfileUpdateEvent
  } = useTrackProfileUpdate();
  const {
    handleSetUserAttributes
  } = useUserAttributes();
  const sumbitForm = async () => {
    startTransition(async () => {
      const response = await updateCustomerProfile(formValues, session);
      if (response?.success) {
        const {
          email,
          firstName,
          lastName,
          phoneNumber
        } = response;
        trackProfileUpdateEvent(formValues);
        const dataToSet = {
          user: {
            email,
            name: firstName,
            lastName,
            phoneNumber
          }
        };
        handleSetUserAttributes(dataToSet);
        await update(dataToSet);
        router.refresh();
      }
      showSnackbar(response);
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    const errors = validation(formValues, t);
    if (Object.keys(errors).length === 0) {
      sumbitForm();
    } else {
      setFormErrors(errors);
      console.log("Failed to submit the form, please check the form fields");
    }
  };
  const toggleChangePassword = useCallback(() => {
    setChangePassword(prev => !prev);
  }, []);
  return <>
      <form onSubmit={handleSubmit} className="py-4 md:px-4">
        <div className="flex flex-wrap">
          {PROFILE_FORM_FIELDS?.map(field => <div key={field.id} className="mb-4 w-full md:w-1/2 md:odd:pe-4">
              {field.type === "input" && <Input type="text" name={field.name} placeholder={t(field.placeholder)} value={formValues?.[field.name]} onChange={handleValidation} readOnly={field.readOnly} disabled={field.disabled} label={t(field.label)} />}
              {field.type === "select" && <Select options={field?.options?.map(option => ({
            ...option,
            label: t(option.label)
          }))} name={field.name} value={formValues?.[field.name]} label={t(field.label)} onChange={handleValidation} />}

              <ErrorMessage message={formErrors?.[field.name]} />
            </div>)}
          {session?.user?.socialType === "none" && <div className="mb-4 w-full md:w-1/2 md:odd:pe-4">
              <label htmlFor="input-field" className="text-supportiveMediumGray mb-2 block text-sm font-medium">
                {t("Password")}
              </label>

              <div className="border-supportiveDarkGray bg-icon-bg h-9 border text-sm md:h-11">
                <button onClick={toggleChangePassword} variant="secondary" className="flex size-full items-center rounded px-4 text-gray-700" type="button">
                  <PasswordDots />
                  <span className="text-anchor-text ms-auto font-medium">
                    {t("Change_Password")}
                  </span>
                </button>
              </div>
            </div>}
        </div>
        <div className="text-center md:text-end">
          <Button size="md" type="submit" variant="primary" disabled={isPending} className="w-full font-semibold md:w-auto" data-sentry-element="Button" data-sentry-source-file="UpdateProfileForm.jsx">
            {isPending ? <LoadingDots className="bg-white" /> : `${t("Update")}`}
          </Button>
        </div>
      </form>
      {/* Drawer for desktop view */}
      <OffCanvasDrawer open={openChangePassword} handleClose={toggleChangePassword} title={t("Change_Password")} position="right" data-sentry-element="OffCanvasDrawer" data-sentry-source-file="UpdateProfileForm.jsx">
        <ChangePasswordForm session={session} toggleChangePassword={toggleChangePassword} data-sentry-element="ChangePasswordForm" data-sentry-source-file="UpdateProfileForm.jsx" />
      </OffCanvasDrawer>
    </>;
};
export default UpdateProfileForm;